import React, { useEffect, useMemo, useRef, useState } from 'react';
import { UsageTimeseriesPoint } from '../../../services/Usage';
import { featureLabel } from './usage-format';
import { useSek } from './sek';

// Categorical palette (validated for CVD separation on light surfaces with the
// dataviz validator). Fixed order, never cycled; the 6th series is "Other".
export const SERIES_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4'];
export const OTHER_COLOR = '#9ca3af';
export const MAX_SERIES = SERIES_COLORS.length;
export const OTHER_KEY = '__other__';

/**
 * Colour assignment follows the entity, not its rank in the current range:
 * the top features overall get a slot; everything else folds into "Other".
 */
export function assignFeatureColors(featuresByCost: string[]): Record<string, string> {
  const colors: Record<string, string> = {};
  featuresByCost.slice(0, MAX_SERIES).forEach((f, i) => (colors[f] = SERIES_COLORS[i]));
  return colors;
}

interface Props {
  timeseries: UsageTimeseriesPoint[];
  /** Feature keys ordered by total cost, descending (legend order) */
  featuresByCost: string[];
  /** Feature -> colour; features without a colour fold into "Other" */
  colors: Record<string, string>;
  startDate: string;
  endDate: string;
}

const HEIGHT = 240;
const MARGIN = { top: 12, right: 8, bottom: 28, left: 56 };
const TOOLTIP_W = 200;

function eachDay(start: string, end: string): string[] {
  const days: string[] = [];
  const d = new Date(start.slice(0, 10) + 'T00:00:00Z');
  const last = new Date(end.slice(0, 10) + 'T00:00:00Z');
  while (d <= last && days.length < 400) {
    days.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return days;
}

/** Round tick step (1, 2, 2.5 or 5 x 10^n) giving about 4 intervals. */
function niceTicks(maxValue: number, target = 4): number[] {
  if (maxValue <= 0) return [0, 1];
  const raw = maxValue / target;
  const exp = Math.pow(10, Math.floor(Math.log10(raw)));
  const n = raw / exp;
  const step = (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * exp;
  const count = Math.ceil(maxValue / step - 1e-9);
  return Array.from({ length: count + 1 }, (_, i) => Number((i * step).toPrecision(12)));
}

const formatDay = (day: string, opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' }) =>
  new Date(day + 'T00:00:00Z').toLocaleDateString('en-GB', { ...opts, timeZone: 'UTC' });

export const CostOverTimeChart: React.FC<Props> = ({ timeseries, featuresByCost, colors, startDate, endDate }) => {
  const formatUsd = useSek();
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [hovered, setHovered] = useState<number | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const seriesKeys = useMemo(() => {
    const colored = featuresByCost.filter((f) => colors[f]);
    const hasOther = featuresByCost.some((f) => !colors[f]);
    return [...colored, ...(hasOther ? [OTHER_KEY] : [])];
  }, [featuresByCost, colors]);

  const days = useMemo(() => {
    const byDay = new Map<string, Record<string, number>>();
    for (const p of timeseries) {
      const key = colors[p.feature] ? p.feature : OTHER_KEY;
      const row = byDay.get(p.date) ?? {};
      row[key] = (row[key] ?? 0) + p.costUsd;
      byDay.set(p.date, row);
    }
    return eachDay(startDate, endDate).map((date) => {
      const values = byDay.get(date) ?? {};
      const total = Object.values(values).reduce((a, b) => a + b, 0);
      return { date, values, total };
    });
  }, [timeseries, colors, startDate, endDate]);

  const ticks = niceTicks(Math.max(0, ...days.map((d) => d.total)));
  const maxTotal = ticks[ticks.length - 1];
  const plotW = Math.max(0, width - MARGIN.left - MARGIN.right);
  const plotH = HEIGHT - MARGIN.top - MARGIN.bottom;
  const slot = days.length ? plotW / days.length : 0;
  const barW = Math.max(2, Math.min(28, slot * 0.7));
  const y = (v: number) => MARGIN.top + plotH - (v / maxTotal) * plotH;
  const labelEvery = Math.max(1, Math.ceil(days.length / Math.max(1, Math.floor(plotW / 64))));
  const legendTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const d of days) for (const [k, v] of Object.entries(d.values)) totals[k] = (totals[k] ?? 0) + v;
    return totals;
  }, [days]);

  const colorOf = (key: string) => (key === OTHER_KEY ? OTHER_COLOR : colors[key]);
  const labelOf = (key: string) => (key === OTHER_KEY ? 'Other' : featureLabel(key));
  const hoveredDay = hovered !== null ? days[hovered] : null;

  return (
    <div>
      {/* Legend: always present for >= 2 series; values in text ink, colour on the swatch */}
      {seriesKeys.length > 1 && (
        <ul className="flex flex-wrap gap-x-4 gap-y-1 mb-3 text-xs text-muted-foreground" aria-label="Features">
          {seriesKeys.map((key) => (
            <li key={key} className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: colorOf(key) }} />
              <span className="text-foreground">{labelOf(key)}</span>
              <span>{formatUsd(legendTotals[key] ?? 0)}</span>
            </li>
          ))}
        </ul>
      )}

      <div ref={containerRef} className="relative w-full" style={{ height: HEIGHT }}>
        {width > 0 && (
          <svg
            width={width}
            height={HEIGHT}
            role="img"
            aria-label="Daily AI cost, stacked by feature"
            onMouseLeave={() => setHovered(null)}
          >
            {/* Recessive grid + y axis labels */}
            {ticks.map((t) => (
              <g key={t}>
                <line x1={MARGIN.left} x2={width - MARGIN.right} y1={y(t)} y2={y(t)} stroke="currentColor" className="text-border" strokeWidth={1} />
                <text x={MARGIN.left - 8} y={y(t)} dy="0.32em" textAnchor="end" className="fill-muted-foreground" fontSize={11}>
                  {formatUsd(t, true)}
                </text>
              </g>
            ))}

            {days.map((day, i) => {
              const cx = MARGIN.left + slot * i + slot / 2;
              let cursor = 0;
              const present = seriesKeys.filter((k) => (day.values[k] ?? 0) > 0);
              return (
                <g key={day.date}>
                  {/* Hit target: the whole column, larger than the mark */}
                  <rect
                    x={MARGIN.left + slot * i}
                    y={MARGIN.top}
                    width={slot}
                    height={plotH}
                    fill={hovered === i ? 'currentColor' : 'transparent'}
                    className="text-muted/60"
                    tabIndex={0}
                    aria-label={`${formatDay(day.date, { day: 'numeric', month: 'long' })}: ${formatUsd(day.total)}`}
                    onMouseEnter={() => setHovered(i)}
                    onFocus={() => setHovered(i)}
                    onBlur={() => setHovered(null)}
                  />
                  {present.map((key, j) => {
                    const v = day.values[key];
                    const top = y(cursor + v);
                    const h = Math.max(1, y(cursor) - top - (j > 0 ? 2 : 0)); // 2px surface gap between segments
                    cursor += v;
                    const isTop = j === present.length - 1;
                    const r = isTop ? Math.min(4, barW / 2, h) : 0;
                    const x = cx - barW / 2;
                    // Rounded data-end on the top segment only; square at the baseline
                    const path = r
                      ? `M${x},${top + h} V${top + r} Q${x},${top} ${x + r},${top} H${x + barW - r} Q${x + barW},${top} ${x + barW},${top + r} V${top + h} Z`
                      : `M${x},${top + h} V${top} H${x + barW} V${top + h} Z`;
                    return <path key={key} d={path} fill={colorOf(key)} pointerEvents="none" />;
                  })}
                  {i % labelEvery === 0 && (
                    <text x={cx} y={HEIGHT - 8} textAnchor="middle" className="fill-muted-foreground" fontSize={11}>
                      {formatDay(day.date)}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
        )}

        {/* Tooltip */}
        {hoveredDay && hovered !== null && (
          <div
            className="pointer-events-none absolute z-10 rounded-md border bg-popover px-3 py-2 text-xs shadow-md"
            style={{
              top: 4,
              width: TOOLTIP_W,
              // Sit beside the hovered column (never over it): right if it fits, else left
              left:
                MARGIN.left + slot * (hovered + 1) + 8 + TOOLTIP_W <= width
                  ? MARGIN.left + slot * (hovered + 1) + 8
                  : Math.max(0, MARGIN.left + slot * hovered - 8 - TOOLTIP_W),
            }}
          >
            <p className="font-medium text-foreground mb-1">
              {formatDay(hoveredDay.date, { weekday: 'short', day: 'numeric', month: 'short' })}
            </p>
            {hoveredDay.total === 0 ? (
              <p className="text-muted-foreground">No usage</p>
            ) : (
              <>
                {seriesKeys
                  .filter((k) => (hoveredDay.values[k] ?? 0) > 0)
                  .map((key) => (
                    <div key={key} className="flex items-center justify-between gap-3">
                      <span className="flex items-center gap-1.5 text-muted-foreground">
                        <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: colorOf(key) }} />
                        {labelOf(key)}
                      </span>
                      <span className="tabular-nums text-foreground">{formatUsd(hoveredDay.values[key])}</span>
                    </div>
                  ))}
                <div className="mt-1 flex justify-between border-t pt-1 font-medium text-foreground">
                  <span>Total</span>
                  <span className="tabular-nums">{formatUsd(hoveredDay.total)}</span>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
